import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useSubscription } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../redux/hooks';
import { CHAT_ROUTE, CLIENT_ROUTE, CLIENTS_ROUTE, LOGIN_ROUTE } from '../../utils/routes';
import {
  ChatMessageStatus,
  IChatList,
  ICoachMessagesPyalod,
  MessageUpdateType,
  SenderType,
  setChatNotificationStatus,
  updateChat,
  updateMessageStatus,
} from '../../redux/chat/chatSlice';
import { SUBSCRIBE_TO_COACH_MESSAGES } from '../../service/graphql/chatQuery';
import { showNotificationToast } from '../Chat/components/Notification';
import { brandLogoKey, brandTagKey } from '../../config/constants';

interface IPrivateRouteProps {
  children: React.ReactElement;
}

const PrivateRoute = ({ children }: IPrivateRouteProps): React.ReactElement => {
  const isAuthenticated = useAppSelector((state) => state.coach.loggedInStatus);
  const loading = useAppSelector((state) => state.coach.loading);
  const userRole = useAppSelector((state) => state.coach.role);
  const location = useLocation();
  const roles = ['coach', 'admin'];
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const [subscriptionData, setSubscriptionData] = useState<IChatList | undefined>(undefined);
  const dispatch = useDispatch();
  const baseClientRoute = CLIENT_ROUTE.replace(':id', '');

  const { data } = useSubscription<ICoachMessagesPyalod>(SUBSCRIBE_TO_COACH_MESSAGES, {
    variables: { coachId },
    skip:
      !coachId || location.pathname === CHAT_ROUTE || location.pathname.includes(baseClientRoute),
  });

  const showBrowserNotification = async (chat: IChatList) => {
    const { message } = chat.chatMessages[0];
    const messageId = chat.chatMessages[0].id;
    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
    }

    const notification = new Notification(`New message from ${chat.client.firstName}`, {
      body: message,
      icon: '/assets/icons/chat-round.svg',
      tag: messageId,
    });

    notification.onclick = () => {
      window.focus();
      window.location.href = CHAT_ROUTE;
    };
  };

  useEffect(() => {
    if (!data) return;

    setSubscriptionData(data.coachMessages);
    dispatch(
      updateMessageStatus({
        messageStatusInput: ChatMessageStatus.DELIVERED,
        chatMessagesId: data.coachMessages.chatMessages[0].id,
      })
    );
  }, [data]);

  useEffect(() => {
    if (
      subscriptionData &&
      !location.pathname.includes(baseClientRoute) &&
      location.pathname !== CHAT_ROUTE
    ) {
      dispatch(updateChat(subscriptionData));
      showNotificationToast(subscriptionData);
      showBrowserNotification(subscriptionData);
      dispatch(setChatNotificationStatus(true));
      setSubscriptionData(undefined);
    }
  }, [subscriptionData, location.pathname]);

  if (loading) {
    const brandLogo = localStorage.getItem(brandLogoKey);
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
      >
        <Box>
          {brandLogo ? (
            <img src={brandLogo} alt="pm-loader" height={100} width={100} />
          ) : (
            <img src="/assets/gifs/pm-loader.gif" alt="pm-loader" height={100} width={100} />
          )}
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated || !roles.includes(userRole)) {
    // Redirect them to the /unauthorized page, but save the current location they were trying to go to
    const brandTag = localStorage.getItem(brandTagKey);
    if (brandTag) {
      return <Navigate to={`${LOGIN_ROUTE}/${brandTag}`} state={{ from: location }} replace />;
    }
    return <Navigate to={LOGIN_ROUTE} state={{ from: location }} replace />;
  }

  return children;
};

export default PrivateRoute;
